import { runtimeConfig } from '#lib/config/runtime-config.js';
import type {
  DearrowThumbnailFallback,
  YoutubeThumbnailPosition,
} from '#lib/settings/preferences.svelte.js';

const VIDEO_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
]);
const VIDEO_ID = /^[\w-]{11}$/;
const TIMESTAMP = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/;

export interface YoutubeVideo {
  id: string;
  start: number | null;
}

export interface YoutubeDetails {
  title: string;
  author: string | null;
}

export interface DearrowDetails {
  title: string | null;
  crowdsourcedThumbnail: number | 'original' | null;
  randomThumbnail: number | null;
}

export interface YoutubeEmbed {
  author: string | null;
  title: string;
  thumbnailUrl: string;
}

// this is a subset of what the API returns
interface DearrowApiTitle {
  title: string;
  votes: number;
  locked: boolean;
}

interface DearrowApiThumbnail {
  timestamp: number | null;
  original: boolean;
  votes: number;
  locked: boolean;
}

interface DearrowApiData {
  titles: DearrowApiTitle[];
  thumbnails: DearrowApiThumbnail[];
  // note: this number is a fraction of the video duration
  //       if the server gives us no video duration, we can't do anything with this
  randomTime: number;
  videoDuration: number | null;
}

function parseStart(value: string | null): number | null {
  if (!value) return null;
  const match = TIMESTAMP.exec(value);
  if (!match) return null;
  const [, hours = '0', minutes = '0', seconds = '0'] = match;
  const total = Number(hours) * 3600 + Number(minutes) * 60 + Number(seconds);
  return total > 0 ? total : null;
}

function videoId(url: URL): string | null {
  if (url.hostname === 'youtu.be') return url.pathname.slice(1);
  if (!VIDEO_HOSTS.has(url.hostname)) return null;
  if (url.pathname === '/watch') return url.searchParams.get('v');
  if (url.pathname.startsWith('/shorts/')) return url.pathname.split('/')[2] ?? null;
  return null;
}

export function parseYoutubeLink(href: string): YoutubeVideo | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;

  const id = videoId(url);
  if (id === null || !VIDEO_ID.test(id)) return null;

  return {
    id,
    start: parseStart(url.searchParams.get('t') ?? url.searchParams.get('start')),
  };
}

export async function youtubePlayerUrl(video: YoutubeVideo): Promise<string> {
  const service = ['http:', 'https:'].includes(location.protocol)
    ? null
    : (await runtimeConfig()).embeds.serviceUrl;
  let url: URL;
  if (service !== null) {
    url = new URL(`${service}/_soliditas/embed/youtube`);
    url.searchParams.set('v', video.id);
  } else {
    url = new URL(`https://www.youtube-nocookie.com/embed/${video.id}`);
    url.searchParams.set('autoplay', '1');
  }
  if (video.start !== null) url.searchParams.set('start', String(video.start));
  return url.href;
}

function youtubeThumbnailUrl(id: string): string {
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}

function youtubeAutogeneratedThumbnailUrl(id: string, type: YoutubeThumbnailPosition): string {
  switch (type) {
    case 'start':
      return `https://i.ytimg.com/vi/${id}/hq1.jpg`;
    case 'middle':
      return `https://i.ytimg.com/vi/${id}/hq2.jpg`;
    case 'end':
      return `https://i.ytimg.com/vi/${id}/hq3.jpg`;
  }
}

function dearrowFallbackThumbnailUrl(
  id: string,
  fallbackType: DearrowThumbnailFallback,
  youtubePosition: YoutubeThumbnailPosition
): string {
  switch (fallbackType) {
    // this function should return a non-dearrow thumbnail url
    // if the user picks 'random' as the fallback, we should fall back onto youtube thumbnails instead of original
    case 'random':
    case 'youtube_autogen':
      return youtubeAutogeneratedThumbnailUrl(id, youtubePosition);
    case 'original':
      return youtubeThumbnailUrl(id);
  }
}

function dearrowGeneratedThumbnailUrl(id: string, timestamp: number, fallbackUrl: string): string {
  const url = new URL('https://dearrow-thumb.ajay.app/api/v1/getThumbnail');
  url.searchParams.set('videoID', id);
  url.searchParams.set('time', timestamp.toString());
  url.searchParams.set('redirectUrl', fallbackUrl);
  return url.href;
}

function dearrowThumbnailUrl(
  id: string,
  dearrow: DearrowDetails | null,
  fallbackType: DearrowThumbnailFallback,
  youtubePosition: YoutubeThumbnailPosition
): string {
  if (dearrow === null) return dearrowFallbackThumbnailUrl(id, fallbackType, youtubePosition);
  if (dearrow.crowdsourcedThumbnail === 'original') return youtubeThumbnailUrl(id);

  const timestamp =
    dearrow.crowdsourcedThumbnail ?? (fallbackType === 'random' ? dearrow.randomThumbnail : null);

  if (timestamp === null) return dearrowFallbackThumbnailUrl(id, fallbackType, youtubePosition);
  return dearrowGeneratedThumbnailUrl(
    id,
    timestamp,
    dearrowFallbackThumbnailUrl(id, fallbackType, youtubePosition)
  );
}

const youtubeDetails = new Map<string, Promise<YoutubeDetails | null>>();
const dearrowDetails = new Map<string, Promise<DearrowDetails | null>>();

async function fetchDetails(id: string): Promise<YoutubeDetails | null> {
  const url = new URL('https://www.youtube.com/oembed');
  url.searchParams.set('format', 'json');
  url.searchParams.set('url', `https://www.youtube.com/watch?v=${id}`);
  const response = await fetch(url);
  if (!response.ok) return null;
  const body = (await response.json()) as { title?: unknown; author_name?: unknown };
  if (typeof body.title !== 'string') return null;
  return {
    title: body.title,
    author: typeof body.author_name === 'string' ? body.author_name : null,
  };
}

async function fetchDearrow(id: string): Promise<DearrowDetails | null> {
  const url = new URL('https://sponsor.ajay.app/api/branding');
  url.searchParams.set('videoID', id);
  url.searchParams.set('service', 'YouTube');
  url.searchParams.set('fetchAll', 'false');
  const response = await fetch(url);
  if (response.status !== 200 && response.status !== 404) {
    throw new Error(`DeArrow API returned unexpected status code ${response.status}`);
  }
  const json = (await response.json()) as DearrowApiData;
  const title = json.titles.find((title) => title.votes >= 0 || title.locked);
  const thumbnail = json.thumbnails.find((thumb) => thumb.votes >= 0 || thumb.locked);
  const randomThumbnail = json.videoDuration ? json.videoDuration * json.randomTime : null;

  return {
    title: title?.title ?? null,
    crowdsourcedThumbnail:
      thumbnail?.original === true ? 'original' : (thumbnail?.timestamp ?? null),
    randomThumbnail,
  };
}

export function loadYoutubeDetails(id: string): Promise<YoutubeDetails | null> {
  const cached = youtubeDetails.get(id);
  if (cached) return cached;

  const promise = fetchDetails(id).catch((error: unknown) => {
    console.warn('[sable youtube embed] youtube unavailable', id, error);
    return null;
  });
  youtubeDetails.set(id, promise);
  return promise;
}

export function loadDearrowDetails(id: string): Promise<DearrowDetails | null> {
  const cached = dearrowDetails.get(id);
  if (cached) return cached;

  const promise = fetchDearrow(id).catch((error: unknown) => {
    console.warn('[sable youtube embed] dearrow unavailable', id, error);
    return null;
  });
  dearrowDetails.set(id, promise);
  return promise;
}

export async function loadDetails(
  id: string,
  useDearrow: boolean,
  fallbackType: DearrowThumbnailFallback,
  youtubePosition: YoutubeThumbnailPosition
): Promise<YoutubeEmbed | null> {
  const [youtube, dearrow] = await Promise.all([
    loadYoutubeDetails(id),
    useDearrow ? loadDearrowDetails(id) : null,
  ]);

  // while we could technically substitute missing youtube data with dearrow data,
  // we'd be adding cases where parts of the embed data are null, and it's better to avoid that entirely
  if (youtube === null) return null;

  // '>' disables the formatter for the following word in dearrow titles, so it is removed.
  const dearrowTitle = dearrow?.title?.replaceAll(/(^| )>/g, '$1');

  return {
    author: youtube.author,
    title: dearrowTitle ?? youtube.title,
    thumbnailUrl: useDearrow
      ? dearrowThumbnailUrl(id, dearrow, fallbackType, youtubePosition)
      : youtubeThumbnailUrl(id),
  };
}
